import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle } from './ui';

const LicenseTypeModal = ({ isOpen, onClose, onSubmit, isLoading = false, editingLicenseType = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_price: '',
    max_installations: '',
    validity_days: '',
    is_promotion: false,
    promotion_name: '',
    promotion_description: '',
    discount_percentage: '',
    promotion_start_date: '',
    promotion_end_date: '',
    is_active: true
  });

  // Update form data when editing
  React.useEffect(() => {
    if (editingLicenseType) {
      setFormData({
        name: editingLicenseType.name || '',
        description: editingLicenseType.description || '',
        base_price: editingLicenseType.base_price || '',
        max_installations: editingLicenseType.max_installations || '',
        validity_days: editingLicenseType.validity_days || '',
        is_promotion: editingLicenseType.is_promotion || false,
        promotion_name: editingLicenseType.promotion_name || '',
        promotion_description: editingLicenseType.promotion_description || '',
        discount_percentage: editingLicenseType.discount_percentage || '',
        promotion_start_date: editingLicenseType.promotion_start_date ? 
          new Date(editingLicenseType.promotion_start_date).toISOString().split('T')[0] : '',
        promotion_end_date: editingLicenseType.promotion_end_date ? 
          new Date(editingLicenseType.promotion_end_date).toISOString().split('T')[0] : '',
        is_active: editingLicenseType.is_active !== undefined ? editingLicenseType.is_active : true
      });
    } else {
      // Reset form for new license type
      setFormData({
        name: '',
        description: '',
        base_price: '',
        max_installations: '',
        validity_days: '',
        is_promotion: false,
        promotion_name: '',
        promotion_description: '',
        discount_percentage: '',
        promotion_start_date: '',
        promotion_end_date: '',
        is_active: true
      });
    }
  }, [editingLicenseType, isOpen]);

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (!formData.base_price || parseFloat(formData.base_price) <= 0) {
      newErrors.base_price = 'El precio base debe ser mayor a 0';
    }

    if (!formData.max_installations || parseInt(formData.max_installations) <= 0) {
      newErrors.max_installations = 'El máximo de instalaciones debe ser mayor a 0';
    }

    if (!formData.validity_days || parseInt(formData.validity_days) <= 0) {
      newErrors.validity_days = 'Los días de validez deben ser mayores a 0';
    }

    if (formData.is_promotion) {
      if (!formData.promotion_name.trim()) {
        newErrors.promotion_name = 'El nombre de promoción es obligatorio';
      }

      if (formData.discount_percentage && (parseFloat(formData.discount_percentage) < 0 || parseFloat(formData.discount_percentage) > 100)) {
        newErrors.discount_percentage = 'El descuento debe estar entre 0 y 100%';
      }

      if (formData.promotion_start_date && formData.promotion_end_date) {
        const startDate = new Date(formData.promotion_start_date);
        const endDate = new Date(formData.promotion_end_date);
        if (startDate >= endDate) {
          newErrors.promotion_end_date = 'La fecha de fin debe ser posterior a la fecha de inicio';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const submitData = {
      ...formData,
      base_price: parseFloat(formData.base_price),
      max_installations: parseInt(formData.max_installations),
      validity_days: parseInt(formData.validity_days),
      discount_percentage: formData.discount_percentage ? parseFloat(formData.discount_percentage) : null,
      promotion_start_date: formData.promotion_start_date || null,
      promotion_end_date: formData.promotion_end_date || null
    };

    console.log('Datos a enviar:', submitData);
    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      base_price: '',
      max_installations: '',
      validity_days: '',
      is_promotion: false,
      promotion_name: '',
      promotion_description: '',
      discount_percentage: '',
      promotion_start_date: '',
      promotion_end_date: '',
      is_active: true
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingLicenseType ? 'Editar Tipo de Licencia' : 'Nuevo Tipo de Licencia'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información Básica */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Información Básica</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Tipo *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ej: Básica, Premium, Empresarial"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descripción del tipo de licencia"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio Base ($) *
                  </label>
                  <Input
                    type="number"
                    name="base_price"
                    value={formData.base_price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className={errors.base_price ? 'border-red-500' : ''}
                  />
                  {errors.base_price && <p className="text-red-500 text-sm mt-1">{errors.base_price}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Máx. Instalaciones *
                  </label>
                  <Input
                    type="number"
                    name="max_installations"
                    value={formData.max_installations}
                    onChange={handleInputChange}
                    placeholder="1"
                    min="1"
                    className={errors.max_installations ? 'border-red-500' : ''}
                  />
                  {errors.max_installations && <p className="text-red-500 text-sm mt-1">{errors.max_installations}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Días de Validez *
                  </label>
                  <Input
                    type="number"
                    name="validity_days"
                    value={formData.validity_days}
                    onChange={handleInputChange}
                    placeholder="365"
                    min="1"
                    className={errors.validity_days ? 'border-red-500' : ''}
                  />
                  {errors.validity_days && <p className="text-red-500 text-sm mt-1">{errors.validity_days}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuración de Promoción */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Configuración de Promoción</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_promotion"
                  checked={formData.is_promotion}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Es una promoción
                </label>
              </div>

              {formData.is_promotion && (
                <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de la Promoción *
                    </label>
                    <Input
                      type="text"
                      name="promotion_name"
                      value={formData.promotion_name}
                      onChange={handleInputChange}
                      placeholder="Ej: Black Friday 2024"
                      className={errors.promotion_name ? 'border-red-500' : ''}
                    />
                    {errors.promotion_name && <p className="text-red-500 text-sm mt-1">{errors.promotion_name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripción de la Promoción
                    </label>
                    <textarea
                      name="promotion_description"
                      value={formData.promotion_description}
                      onChange={handleInputChange}
                      placeholder="Descripción de la promoción"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descuento (%)
                      </label>
                      <Input
                        type="number"
                        name="discount_percentage"
                        value={formData.discount_percentage}
                        onChange={handleInputChange}
                        placeholder="20"
                        min="0"
                        max="100"
                        step="0.01"
                        className={errors.discount_percentage ? 'border-red-500' : ''}
                      />
                      {errors.discount_percentage && <p className="text-red-500 text-sm mt-1">{errors.discount_percentage}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha de Inicio
                      </label>
                      <Input
                        type="date"
                        name="promotion_start_date"
                        value={formData.promotion_start_date}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha de Fin
                      </label>
                      <Input
                        type="date"
                        name="promotion_end_date"
                        value={formData.promotion_end_date}
                        onChange={handleInputChange}
                        className={errors.promotion_end_date ? 'border-red-500' : ''}
                      />
                      {errors.promotion_end_date && <p className="text-red-500 text-sm mt-1">{errors.promotion_end_date}</p>}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Estado */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Tipo de licencia activo
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading 
                ? (editingLicenseType ? 'Actualizando...' : 'Creando...') 
                : (editingLicenseType ? 'Actualizar Tipo de Licencia' : 'Crear Tipo de Licencia')
              }
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LicenseTypeModal;
